import React from 'react'
import { cn } from '../../lib/utils'

interface PlaceholderFeature {
  name: string
  description: string
}

interface PlaceholderPageProps {
  title: string
  description: string
  icon: React.ComponentType<{ className?: string }>
  features?: PlaceholderFeature[]
  accentColor?: 'orange' | 'blue' | 'emerald' | 'violet' | 'amber' | 'rose'
}

const accentStyles = {
  orange: {
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
    badgeBg: 'bg-orange-100',
    badgeText: 'text-orange-700',
    featureIcon: 'text-orange-500',
    border: 'border-orange-200',
  },
  blue: {
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    badgeBg: 'bg-blue-100',
    badgeText: 'text-blue-700',
    featureIcon: 'text-blue-500',
    border: 'border-blue-200',
  },
  emerald: {
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    badgeBg: 'bg-emerald-100',
    badgeText: 'text-emerald-700',
    featureIcon: 'text-emerald-500',
    border: 'border-emerald-200',
  },
  violet: {
    iconBg: 'bg-violet-100',
    iconColor: 'text-violet-600',
    badgeBg: 'bg-violet-100',
    badgeText: 'text-violet-700',
    featureIcon: 'text-violet-500',
    border: 'border-violet-200',
  },
  amber: {
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    badgeBg: 'bg-amber-100',
    badgeText: 'text-amber-700',
    featureIcon: 'text-amber-500',
    border: 'border-amber-200',
  },
  rose: {
    iconBg: 'bg-rose-100',
    iconColor: 'text-rose-600',
    badgeBg: 'bg-rose-100',
    badgeText: 'text-rose-700',
    featureIcon: 'text-rose-500',
    border: 'border-rose-200',
  },
}

export default function PlaceholderPage({
  title,
  description,
  icon: Icon,
  features,
  accentColor = 'orange',
}: PlaceholderPageProps) {
  const styles = accentStyles[accentColor]

  return (
    <div className="h-full flex flex-col overflow-hidden bg-slate-50">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200">
        <div className="max-w-[1800px] mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', styles.iconBg)}>
              <Icon className={cn('h-5 w-5', styles.iconColor)} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
              <p className="text-sm text-slate-500">{description}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[1800px] mx-auto px-6 py-8">
          {/* Coming Soon Card */}
          <div className={cn('rounded-xl border bg-white shadow-sm overflow-hidden', styles.border)}>
            <div className="p-8 sm:p-12 text-center">
              {/* Large Icon */}
              <div className={cn('w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6', styles.iconBg)}>
                <Icon className={cn('h-10 w-10', styles.iconColor)} />
              </div>

              {/* Badge */}
              <div className="inline-flex items-center mb-4">
                <span className={cn('px-3 py-1 rounded-full text-xs font-semibold', styles.badgeBg, styles.badgeText)}>
                  Coming Soon
                </span>
              </div>

              {/* Title */}
              <h3 className="text-2xl font-bold text-slate-900 mb-2">{title}</h3>
              <p className="text-slate-500 max-w-md mx-auto mb-8">
                {description}. This feature is currently under development and will be available soon.
              </p>

              {/* Feature Grid */}
              {features && features.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg mx-auto text-left">
                  {features.map((feature, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-3 p-3 rounded-lg bg-slate-50"
                    >
                      <div className={cn('w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5', styles.iconBg)}>
                        <svg
                          className={cn('w-3 h-3', styles.featureIcon)}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2.5}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          {feature.name}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
